// src/pages/ChatsExport.jsx
import React, { useState, useEffect, useCallback } from "react";
import { formatDate } from "../utils/dateHelpers";
import { useNavigate } from "react-router-dom";
import { collection, query, orderBy, limit, getDocs, startAfter, getCountFromServer } from "firebase/firestore";
import { signInWithPopup, GoogleAuthProvider } from "firebase/auth";
import { auth, db } from "../firebase";
import useAuth from "../components/useCurrentUser";
import ExportButtons from "../components/ExportButtons";
import PageHeader from "../components/PageHeader";
import { useOrganization } from "../context/OrganizationContext";
import { useTranslation } from 'react-i18next';

export default function ChatsExport() {
  const navigate = useNavigate();
  const { context } = useOrganization();
  const { user, loading, isAuthenticated, email, uid, signOut } = useAuth();
  const [chats, setChats] = useState([]);
  const [selectedChatID, setSelectedChatID] = useState("");
  const [loadingChats, setLoadingChats] = useState(false);
  // `totalChats` sale de un count() del servidor, NO del largo de lo cargado. La
  // diferencia no es cosmética: la lista viene topada, así que contar lo cargado hacía
  // que la tarjeta dijera 50 mientras la exportación devolvía 193.
  const [totalChats, setTotalChats] = useState(null);
  const [mensajesDelChat, setMensajesDelChat] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [hayMas, setHayMas] = useState(false);
  const [cargandoMas, setCargandoMas] = useState(false);
  const { t } = useTranslation('chats');
  const { t: tc } = useTranslation('common');

  /** La colección de chats del tenant. Misma resolución que usa el inbox. */
  const coleccionDeChats = useCallback(() => {
    const enOrg = context.mode === 'organization' && context.orgId;
    return enOrg
      ? collection(db, "organizations", context.orgId, "chats")
      : collection(db, "clientes", uid, "chats");
  }, [context.mode, context.orgId, uid]);

  const PAGINA = 50;

  /** Mapea un doc de Firestore a la fila que pinta la lista. */
  const aFila = useCallback((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      nombre: data.nombre || t('list.noName'),
      numero: data.numero || doc.id,
      ultimoMensaje: data.ultimoMensaje || "",
      actualizadoEn: data.actualizadoEn?.toDate?.() || null,
    };
  }, [t]);

  // Cargar lista de chats del usuario
  useEffect(() => {
    if (!isAuthenticated || !uid) return;

    const loadChats = async () => {
      setLoadingChats(true);
      try {
        const chatsRef = coleccionDeChats();

        // EL TOTAL, DEL SERVIDOR. `getCountFromServer` es una agregación: devuelve el
        // número sin traerse los documentos, así que decir la verdad no cuesta 193
        // lecturas. Va en paralelo con la primera página porque no dependen entre sí.
        const [conteo, snapshot] = await Promise.all([
          getCountFromServer(chatsRef),
          getDocs(query(chatsRef, orderBy("actualizadoEn", "desc"), limit(PAGINA))),
        ]);

        setTotalChats(conteo.data().count);
        setChats(snapshot.docs.map(aFila));
        setCursor(snapshot.docs[snapshot.docs.length - 1] || null);
        // Una página corta significa que no hay más. Una llena no lo garantiza, pero
        // pedir una de más y que venga vacía es más barato que esconder chats.
        setHayMas(snapshot.size === PAGINA);
      } catch (error) {
        console.error("Error loading chats:", error);
      } finally {
        setLoadingChats(false);
      }
    };

    loadChats();
  }, [isAuthenticated, uid, context, coleccionDeChats, aFila]);

  /**
   * Trae la siguiente página.
   *
   * La lista es un SELECTOR: con 193 chats y un tope de 50, no se podía elegir el
   * número 51 para exportar sus mensajes — o sea que la página no servía para lo que
   * existe. Etiquetar el tope habría sido honesto y habría dejado el callejón sin
   * salida en pie. Botón explícito y no scroll infinito: en un selector, cargar más
   * es una decisión del usuario, no un efecto de mover la rueda.
   */
  const cargarMas = useCallback(async () => {
    if (!cursor || cargandoMas) return;
    setCargandoMas(true);
    try {
      const snapshot = await getDocs(query(
        coleccionDeChats(), orderBy("actualizadoEn", "desc"), startAfter(cursor), limit(PAGINA)
      ));
      const nuevos = snapshot.docs.map(aFila);
      // Se deduplica por id: si a un chat le llega un mensaje mientras paginamos, salta
      // a la primera posición y volvería a aparecer.
      setChats((prev) => {
        const vistos = new Set(prev.map((c) => c.id));
        return [...prev, ...nuevos.filter((c) => !vistos.has(c.id))];
      });
      setCursor(snapshot.docs[snapshot.docs.length - 1] || cursor);
      setHayMas(snapshot.size === PAGINA);
    } catch (error) {
      console.error("Error cargando más chats:", error);
      setHayMas(false);
    } finally {
      setCargandoMas(false);
    }
  }, [cursor, cargandoMas, coleccionDeChats, aFila]);

  /**
   * Cuántos mensajes tiene el chat SELECCIONADO.
   *
   * La tarjeta decía "Total Mensajes" y sumaba `data.mensajesCount`, un campo que NO
   * EXISTE: cero de 193 documentos lo tienen y no hay un solo escritor en el árbol. O
   * sea que mostraba 0 desde siempre.
   *
   * No se sustituye por el total de la organización porque no hay forma barata y
   * correcta de saberlo: exigiría un count() por chat (193 viajes), `usage.messagesThisMonth`
   * es el contador de FACTURACIÓN del ciclo —no el total almacenado—, y un
   * collectionGroup filtrado por `organizationId` dejaría fuera más de la mitad de los
   * mensajes, porque sólo la forma unificada lo lleva.
   *
   * Lo que sí es barato y además es el número que importa donde estás: los mensajes del
   * chat que estás a punto de exportar. Un solo count().
   */
  useEffect(() => {
    if (!selectedChatID) { setMensajesDelChat(null); return; }
    let cancelado = false;
    (async () => {
      try {
        const c = await getCountFromServer(collection(coleccionDeChats(), selectedChatID, "mensajes"));
        if (!cancelado) setMensajesDelChat(c.data().count);
      } catch (e) {
        console.error("Error contando mensajes:", e);
        if (!cancelado) setMensajesDelChat(null);
      }
    })();
    return () => { cancelado = true; };
  }, [selectedChatID, coleccionDeChats]);

  const handleLogin = async () => {
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error("Error signing in:", error);
      alert(tc('login.googleSignInError') + ': ' + error.message);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingBox}>
          <h2>{tc('loading')}</h2>
          <p>{t('export.verifyingAuth')}</p>
        </div>
      </div>
    );
  }

  // Not authenticated
  if (!isAuthenticated) {
    return (
      <div style={styles.container}>
        <div style={styles.loginBox}>
          <h1>{t('export.title')}</h1>
          <p>{t('export.loginSubtitle')}</p>
          <button onClick={handleLogin} style={styles.loginButton}>
            <span style={styles.googleIcon}>G</span>
            {tc('login.googleSignIn')}
          </button>
        </div>
      </div>
    );
  }

  // Authenticated - Main UI
  return (
    <div style={styles.container}>
      <PageHeader title={t('export.title')} subtitle={t('export.subtitle')} />

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <div style={styles.userInfo}>
          <span>{email}</span>
          <button onClick={signOut} style={styles.logoutButton}>
            {tc('logout')}
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          {/* El total del servidor, no el largo de lo cargado. `—` mientras llega:
              un 0 provisional se lee como "no tienes chats". */}
          <div style={styles.statNumber}>{totalChats === null ? '—' : totalChats}</div>
          <div style={styles.statLabel}>{t('export.totalChats')}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statNumber}>{mensajesDelChat === null ? '—' : mensajesDelChat}</div>
          <div style={styles.statLabel}>{t('export.messagesInChat')}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statNumber}>{selectedChatID ? "1" : "0"}</div>
          <div style={styles.statLabel}>{t('export.chatSelected')}</div>
        </div>
      </div>

      {/* Main Content */}
      <div style={styles.mainGrid}>
        {/* Chat Selector */}
        <div style={styles.panel}>
          <h2 style={styles.panelTitle}>{t('export.selectChat')}</h2>
          {loadingChats ? (
            <p>{t('export.loadingChats')}</p>
          ) : (
            <div style={styles.chatList}>
              {chats.length === 0 ? (
                <p style={styles.emptyMessage}>{t('list.empty')}</p>
              ) : (
                chats.map(chat => (
                  <div
                    key={chat.id}
                    onClick={() => setSelectedChatID(chat.id)}
                    style={{
                      ...styles.chatItem,
                      ...(selectedChatID === chat.id ? styles.chatItemSelected : {})
                    }}
                  >
                    <div style={styles.chatItemHeader}>
                      <strong>{chat.nombre}</strong>
                      <span style={styles.chatItemNumber}>{chat.numero}</span>
                    </div>
                    <div style={styles.chatItemMessage}>
                      {chat.ultimoMensaje ? chat.ultimoMensaje.substring(0, 50) + "..." : t('list.noMessages')}
                    </div>
                    {chat.actualizadoEn && (
                      <div style={styles.chatItemDate}>
                        {formatDate(chat.actualizadoEn.toDate ? chat.actualizadoEn.toDate() : chat.actualizadoEn)}
                      </div>
                    )}
                  </div>
                ))
              )}

              {hayMas && (
                <button
                  onClick={cargarMas}
                  disabled={cargandoMas}
                  style={styles.loadMoreButton}
                >
                  {cargandoMas
                    ? t('export.loadingChats')
                    : t('export.loadMore', { cargados: chats.length, total: totalChats ?? '?' })}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Export Options */}
        <div style={styles.panel}>
          <h2 style={styles.panelTitle}>{t('export.exportOptions')}</h2>
          
          {/* Export All Chats */}
          <div style={styles.exportSection}>
            <h3 style={styles.sectionTitle}>{t('export.exportChatList')}</h3>
            <p style={styles.sectionDescription}>
              {t('export.exportChatListDesc')}
            </p>
            <ExportButtons clienteID={uid} chatID="" organizationId={context.mode === 'organization' ? context.orgId : null} />
          </div>

          {/* Export Selected Chat */}
          <div style={styles.exportSection}>
            <h3 style={styles.sectionTitle}>{t('export.exportMessages')}</h3>
            {selectedChatID ? (
              <>
                <p style={styles.sectionDescription}>
                  {t('export.selectedChat')} <strong>{chats.find(c => c.id === selectedChatID)?.nombre || selectedChatID}</strong>
                </p>
                <ExportButtons clienteID={uid} chatID={selectedChatID} organizationId={context.mode === 'organization' ? context.orgId : null} />
              </>
            ) : (
              <p style={styles.sectionDescription}>
                {t('export.selectChatHint')}
              </p>
            )}
          </div>

          {/* Quick Actions */}
          <div style={styles.quickActions}>
            <button
              onClick={() => setSelectedChatID("")}
              style={styles.clearButton}
              disabled={!selectedChatID}
            >
              {t('export.clearSelection')}
            </button>
            <button
              onClick={() => navigate("/dashboard")}
              style={styles.backButton}
            >
              {tc('backToDashboard').replace('\u2190 ', '')}
            </button>
          </div>
        </div>
      </div>

      {/* Help Section */}
      <div style={styles.helpSection}>
        <h3>{t('export.helpTitle')}</h3>
        <ul style={styles.helpList}>
          {t('export.helpItems', { returnObjects: true }).map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// Estilos
const styles = {
  container: {
    minHeight: "100vh",
    backgroundColor: "#f5f7fa",
    padding: "clamp(12px, 3vw, 20px)",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    boxSizing: "border-box",
    width: "100%",
    maxWidth: "1200px",
    margin: "0 auto"
  },
  
  // Loading & Login
  loadingBox: {
    background: "white",
    borderRadius: "12px",
    padding: "40px",
    textAlign: "center",
    maxWidth: "400px",
    margin: "100px auto",
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)"
  },
  loginBox: {
    background: "white",
    borderRadius: "12px",
    padding: "40px",
    textAlign: "center",
    maxWidth: "400px",
    margin: "100px auto",
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)"
  },
  loginButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "10px",
    padding: "12px 24px",
    fontSize: "16px",
    backgroundColor: "#4285f4",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    marginTop: "20px"
  },
  googleIcon: {
    background: "white",
    color: "#4285f4",
    padding: "4px 8px",
    borderRadius: "4px",
    fontWeight: "bold"
  },
  
  // Header
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "30px",
    flexWrap: "wrap",
    gap: "20px"
  },
  title: {
    fontSize: "32px",
    margin: "0",
    color: "#1a202c"
  },
  subtitle: {
    color: "#718096",
    margin: "5px 0 0 0"
  },
  userInfo: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    background: "white",
    padding: "10px 20px",
    borderRadius: "8px"
  },
  logoutButton: {
    padding: "6px 12px",
    background: "#e53e3e",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "14px"
  },
  
  // Stats
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(140px, 100%), 1fr))",
    gap: "12px",
    marginBottom: "24px"
  },
  loadMoreButton: {
    width: '100%',
    padding: '0.6rem',
    marginTop: '0.5rem',
    borderRadius: '8px',
    border: '1px dashed #cbd5e0',
    background: '#f7fafc',
    color: '#4a5568',
    fontSize: '13px',
    cursor: 'pointer',
  },
  statCard: {
    background: "white",
    padding: "20px",
    borderRadius: "12px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    textAlign: "center"
  },
  statNumber: {
    fontSize: "36px",
    fontWeight: "bold",
    color: "#2d3748"
  },
  statLabel: {
    color: "#718096",
    fontSize: "14px",
    marginTop: "5px"
  },
  
  // Main Grid
  mainGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(300px, 100%), 1fr))",
    gap: "16px",
    marginBottom: "24px"
  },
  panel: {
    background: "white",
    borderRadius: "12px",
    padding: "25px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
  },
  panelTitle: {
    fontSize: "20px",
    marginTop: "0",
    marginBottom: "20px",
    color: "#2d3748"
  },
  
  // Chat List
  chatList: {
    maxHeight: "400px",
    overflowY: "auto",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    padding: "10px"
  },
  chatItem: {
    padding: "12px",
    marginBottom: "8px",
    border: "1px solid #e2e8f0",
    borderRadius: "6px",
    cursor: "pointer",
    transition: "all 0.2s",
    "&:hover": {
      backgroundColor: "#f7fafc"
    }
  },
  chatItemSelected: {
    backgroundColor: "#ebf8ff",
    borderColor: "#4299e1"
  },
  chatItemHeader: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "4px"
  },
  chatItemNumber: {
    fontSize: "12px",
    color: "#718096"
  },
  chatItemMessage: {
    fontSize: "14px",
    color: "#4a5568",
    marginBottom: "4px"
  },
  chatItemDate: {
    fontSize: "12px",
    color: "#a0aec0"
  },
  emptyMessage: {
    textAlign: "center",
    color: "#718096",
    padding: "20px"
  },
  
  // Export Sections
  exportSection: {
    marginBottom: "30px",
    padding: "20px",
    background: "#f7fafc",
    borderRadius: "8px"
  },
  sectionTitle: {
    fontSize: "18px",
    marginTop: "0",
    marginBottom: "10px",
    color: "#2d3748"
  },
  sectionDescription: {
    color: "#4a5568",
    marginBottom: "15px"
  },
  
  // Quick Actions
  quickActions: {
    display: "flex",
    gap: "10px",
    marginTop: "20px"
  },
  clearButton: {
    flex: 1,
    padding: "10px",
    background: "#edf2f7",
    color: "#2d3748",
    border: "1px solid #cbd5e0",
    borderRadius: "6px",
    cursor: "pointer"
  },
  backButton: {
    flex: 1,
    padding: "10px",
    background: "#4299e1",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer"
  },
  
  // Help Section
  helpSection: {
    background: "#f0fff4",
    border: "1px solid #9ae6b4",
    borderRadius: "8px",
    padding: "20px"
  },
  helpList: {
    margin: "10px 0",
    paddingLeft: "20px",
    color: "#2f855a"
  },
  
  // Responsive
  "@media (max-width: 768px)": {
    mainGrid: {
      gridTemplateColumns: "1fr"
    }
  }
};